/**
 * Convert a number to a decimal string with the given number of fraction digits
 * @param {number} value
 * @param {number} [fractionDigits=2]
 */
export function toDecimal(value, fractionDigits = 2) {
	return Number.isFinite(value) ? parseFloat(value.toFixed(fractionDigits)) : 0;
}

const MONEY_METRICS = new Set([
	"revenue",
	"cost",
	"profit",
	"average_revenue",
	"ecpm",
	"ecpc",
	"epc",
]);

const PERCENT_METRICS = new Set([
	"cr",
	"cvr",
	"reported_cr",
	"profit_percent",
	"blocked_percent",
	"reject_pct",
	"pa_rejections_pct",
	"dsp_percent",
	"e1_per_ins",
	"kpi",
]);

/**
 * Formats a count / generic numeric cell (comma separators), like ReportsWidget.
 * @param {unknown} n
 */
export function formatCell(n) {
	if (n == null || n === "") {
		return "-";
	}
	const num = Number(n);
	return Number.isFinite(num) ? num.toLocaleString() : String(n);
}

/**
 * Formats a money metric with $ and comma separators.
 * @param {unknown} n
 */
export function formatMoney(n) {
	if (n == null || n === "") {
		return "-";
	}
	const val = Number(n);
	if (!Number.isFinite(val)) {
		return String(n);
	}
	return val >= 1000
		? `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
		: `$${val.toFixed(2)}`;
}

/**
 * Rounds a payout to four decimal places so 0.0005 is kept.
 * @param {unknown} value
 */
export function roundPayout(value) {
	return Number.parseFloat(Number(value).toFixed(4));
}

/**
 * Formats a payout amount with up to four decimals so 0.0005 stays visible.
 * @param {unknown} n
 */
export function formatPayout(n) {
	const val = Number(n);
	if (!Number.isFinite(val)) {
		return String(n);
	}
	return val.toFixed(4).replace(/(?:\.0+|(?:(\.\d*?)0+))$/, "$1");
}

/**
 * Limits a decimal input to the supplied number of fraction digits.
 * @param {unknown} value
 * @param {number} [fractionDigits=4]
 */
export function limitDecimalPlaces(value, fractionDigits = 4) {
	const text = String(value ?? "");
	const decimalIndex = text.indexOf(".");
	if (decimalIndex === -1) {
		return text;
	}
	return text.slice(0, decimalIndex + fractionDigits + 1);
}

/**
 * Formats CR / ratio display to 3 decimal places. If that would round a
 * non-zero rate to zero, precision expands only to the first non-zero decimal
 * place (for example, 0.000832 becomes 0.0008).
 * @param {unknown} v
 */
export function formatCr(v) {
	if (v == null || v === "") {
		return "-";
	}
	const num = Number(v);
	if (!Number.isFinite(num)) {
		return String(v);
	}
	if (num === 0) {
		return "0";
	}
	const absolute = Math.abs(num);
	const firstNonZeroDecimal = absolute < 1 ? Math.ceil(-Math.log10(absolute)) : 0;
	const fractionDigits = Math.max(3, firstNonZeroDecimal);
	if (fractionDigits > 100) {
		return num.toExponential(0);
	}
	const decimal = num.toFixed(fractionDigits).replace(/(?:\.0+|(?:(\.\d*?)0+))$/, "$1");
	return decimal;
}

/**
 * Formats a dashboard / report metric cell by metric name.
 * @param {string} metric
 * @param {unknown} value
 */
export function formatMetricValue(metric, value) {
	if (MONEY_METRICS.has(metric)) {
		return formatMoney(value);
	}
	if (PERCENT_METRICS.has(metric)) {
		return formatCr(value);
	}
	return formatCell(value);
}
